import {
  OpportunitySearchQueryState,
  OrganisationSearchQueryState,
  useGlobal,
} from "@skv/state";
import * as React from "react";

import { useAnalyticsUtil } from "./AnalyticsProvider";

type InputMethods = "auto-suggest" | "typed";
export interface AnalyticsData {
  // main/search results
  subSection?: string;
  subSubSection?: string;
  subSubSubSection?: string;
  pageTitle?: string;
  pageType?: string;
  loginStatus?: "loggedin" | "loggedout";
  searchTerm?: string;
  searchTermInputMethod?: InputMethods;
  searchLocation?: string;
  searchLocationInputMethod?: InputMethods;
  searchLocationRadius?: string; // {distance}km
  searchCategory?: string;
  searchWorkType?: string;
  searchSuitability?: string;
  searchCommitment?: string;
  searchDayAndTime?: string;

  // opp/org
  organisationId?: number;
  opportunityId?: number;
  applicationId?: number;
}

interface AnalyticsProps {
  data: AnalyticsData;
}

export const Analytics: React.FC<AnalyticsProps> = ({ data }) => {
  const { trackPageView } = useAnalyticsUtil();

  if (!trackPageView) {
    throw Error(
      "<Analytics /> component used outside an <AnalyticsProvider />. Did you forget to wrap your app with the AnalyticsProvider component?"
    );
  }

  React.useEffect(() => {
    trackPageView(data);
  }, []);

  return null;
};

interface AnalyticsSearchQueryProps {
  searchQuery: OpportunitySearchQueryState | OrganisationSearchQueryState;
  data?: AnalyticsData;
  delay?: number;
}

/**
 * AnalyticsSearchQuery relies on history.state for the searchQuery.
 * We are doing a history.push AFTER the FETCH:END method has completed,
 * therefore, our searchQuery will actually not be the correct searchQuery
 * when the searchResults has updated (as we have not pushed the new url and
 * searchQuery state yet). Without changing the logic for the searchQuery,
 * for now opt to delay the firing of the tracking event until the searchQuery
 * is stable, which should happen almost immediately, as it'll fire in the next
 * promise block.
 *
 * Side note, this also prevents quick successions of searchQuery changing and
 * firing tracking events (e.g, a user changing category sidebar filters quickly)
 * */
export const AnalyticsSearchQuery: React.FC<AnalyticsSearchQueryProps> = ({
  searchQuery,
  data,
  delay = 1000,
}) => {
  const { trackPageView } = useAnalyticsUtil();

  const {
    categoryList,
    commitmentList,
    dayAndTimeList,
    suitableForList,
    typeOfWorkList,
  } = useGlobal();

  if (!trackPageView) {
    throw Error(
      "<Analytics /> component used outside an <AnalyticsProvider />. Did you forget to wrap your app with the AnalyticsProvider component?"
    );
  }

  React.useEffect(() => {
    const timeout = setTimeout(() => {
      const _data: AnalyticsData = Object.assign(
        {},
        {
          searchTerm: searchQuery.keyword || "",
          searchTermInputMethod: "typed", // todo : when category search is in, work on auto-suggest
          searchLocation: searchQuery.where || "",
          searchLocationInputMethod: "typed",
          searchLocationRadius: "0km", // todo : no radius search atm
          searchCategory: searchQuery.category
            .map((id) => categoryList.find((n) => n.id === id)?.name)
            .filter((n) => n != null)
            .join(", "),
        },
        data
      );

      if ("typeofwork" in searchQuery) {
        _data.searchWorkType = searchQuery.typeofwork
          .map((id) => typeOfWorkList.find((n) => n.id === id)?.name)
          .filter((n) => n != null)
          .join(", ");
      }

      if ("suitablefor" in searchQuery) {
        _data.searchSuitability = searchQuery.suitablefor
          .map((id) => suitableForList.find((n) => n.id === id)?.name)
          .filter((n) => n != null)
          .join(", ");
      }

      if ("commitment" in searchQuery) {
        _data.searchCommitment = searchQuery.commitment
          .map((id) => commitmentList.find((n) => n.id === id)?.name)
          .filter((n) => n != null)
          .join(", ");
      }

      if ("daytime" in searchQuery) {
        _data.searchDayAndTime = searchQuery.daytime
          .map((id) => dayAndTimeList.find((n) => n.id === id)?.name)
          .filter((n) => n != null)
          .join(", ");
      }

      trackPageView(_data);
    }, delay);

    return (): void => {
      clearTimeout(timeout);
    };
  }, [
    categoryList,
    commitmentList,
    data,
    dayAndTimeList,
    searchQuery,
    suitableForList,
    trackPageView,
    typeOfWorkList,
    delay,
  ]);

  return null;
};
